/**
 * Which byte figures one Cleanup activity row states, and under which label.
 *
 * ── Why this is its own function ────────────────────────────────────────
 *
 * The same reason `cleanupBellFigures` is (see
 * `features/agent/api/cleanup-bell-figures.ts`): the rule is a CHOICE
 * between figures that look alike, and a choice can only be guarded by
 * exercising it with values. Nine versions of a source-reading guard on
 * that side each failed to hold the rule it named, twice letting through a
 * mutant that stated a ceiling as a floor. This folder already answers that
 * the same way — `cleanupVerdict`, `unconfirmedRow`, `waitingSince` are
 * rules lifted out of their components for exactly this.
 *
 * ── Three figures, and what each may be said as ─────────────────────────
 *
 * `releasedBytes` — what left the MEMBERS' OWN figures, by the rules their
 *   Storage page counts with: deduped per content hash WITHIN each member,
 *   drive files excluded. A LOWER bound (a killed pass loses its chunk), so
 *   the copy says "at least".
 * `orgReleasedBytes` — what left the ORGANISATION's figure: one charge per
 *   content hash for the WHOLE tenant, and nothing at all while any member
 *   still has that file. Always <= the members' figure.
 * `freedBytes` — what the deleted conversations HELD. A plain sum: the same
 *   file in two of them counts twice and one that is also a drive file
 *   counts at all. The CEILING, and its label says "up to".
 *
 * ── The rules ───────────────────────────────────────────────────────────
 *
 *  1. No figure is stated at zero, and `null` means "nothing measured it" —
 *     never 0. A non-event stated as an event is how this feature misled
 *     people in the first place.
 *  2. The organisation's figure is stated only where it is BELOW the
 *     members' figure AND adds something, and
 *     then ALWAYS WITH the sentence that explains the gap (D41, Season
 *     2026-10-27 option 乙). "Members got back 500 MB, the organisation got
 *     back 180 MB" with nothing between them reads as a bug; the note and
 *     the second figure are one decision, never two.
 *  3. "Adds something" is judged ON THE RENDERED LABELS, not on raw bytes.
 *     `formatBytes` rounds, so two figures a few hundred bytes apart render
 *     the same string — and stating "500 MB · 500 MB" with a paragraph
 *     explaining the difference is worse than stating it once.
 *  3b. BELOW is judged on the RAW BYTES, and it is a separate test from
 *     rule 3 — review round 15 (D41).
 *
 *     `org <= released` is true of the two QUERIES for one input (the
 *     tenant's survivor test is strictly stronger than each member's, and
 *     it charges a hash once at a size no larger). It is NOT true of the
 *     two COLUMNS, because the chunk computes them in two independent
 *     `try` blocks so that losing one figure does not lose the other: a
 *     chunk whose member query times out leaves `released_bytes` where it
 *     was while `org_released_bytes` still accumulates. Three chunks, one
 *     timeout in the middle, and the row holds released 500 MB / org
 *     1.08 GB.
 *
 *     Stated, that row reads "members' usage dropped by at least 500 MB ·
 *     the organisation got back 1.1 GB" under a paragraph explaining why
 *     the organisation's number is the SMALLER one — two figures that
 *     cannot both be true plus an explanation pointing the wrong way. So a
 *     figure that contradicts its own sentence is "no figure to state",
 *     which is rule 1, and the row falls back to stating the members'
 *     figure alone.
 *
 *     Guarded HERE and not clamped in the chunk on purpose: clamping would
 *     hide the measurement, which is the honest record of what each query
 *     answered. This hides only the CLAIM.
 *
 *     `<` versus `<=` is UNOBSERVABLE here, and measured as such rather
 *     than assumed: `formatBytes` is a function of the number alone, so
 *     equal bytes render equal labels and rule 3 already rejects that row.
 *     Both spellings pass all ten cases. `<` is kept because it is what
 *     the rule says; nobody should add a case to pin a distinction the
 *     output cannot show.
 *  5. EACH FIGURE CARRIES ITS OWN HEDGE, and the two are decided
 *     separately (Season 2026-10-27 "做乙").
 *
 *     Each chunk's measurement is exact; what makes a TOTAL a lower bound
 *     is a whole chunk going missing — a figure query that failed both
 *     attempts (its deletes committed, and nothing can recompute it: the
 *     rows it needed are gone), or a rolling deploy whose earlier passes
 *     ran on a worker with no such column. The booking row now records
 *     that, per figure, so a row says the exact number where it IS exact
 *     and hedges only where something was dropped — instead of hedging
 *     every row forever to cover the rare one.
 *
 *     `null` on a loss flag is NOT `false`: it means nobody wrote it (a
 *     booking older than 0348, or one that recorded no chunk), so it
 *     HEDGES — the behaviour before 0348, exactly. Stating a short figure as
 *     exact is the one direction this may not be wrong in.
 *
 *     The two flags are separate because the two queries are, and are lost
 *     independently; one shared flag would hedge a figure that is exact.
 *
 *     ⚠️ AND THE RUN HAS TO BE OVER — review round 16, and this was the
 *     common path, not the rare one. A clean first chunk writes
 *     `released_lost = false` immediately, so "no chunk was dropped" is
 *     true of a booking that has barely started. Judged on the flags
 *     alone, a 520-conversation cleanup 25 deletions in showed
 *
 *         At least 25 of 520 conversations deleted so far ·
 *         members' usage dropped by 120 MB
 *
 *     — the COUNT hedging and the BYTES not, on one line, about the same
 *     partial progress; refresh a minute later and the "exact" figure is
 *     240 MB. "Complete" needs both halves: no chunk lost AND nothing
 *     left to run. `settled` carries the second, and a row still moving
 *     takes the hedged keys whatever its flags say.
 *  4. With no members' figure there is nothing to compare the organisation's
 *     against, so the row falls back to the ceiling exactly as before 0348.
 *     The organisation's figure is never shown alone: on its own it would be
 *     read as what the member got back, which is the overstatement these two
 *     columns exist to remove.
 */
/**
 * ONE copy key and the size to interpolate into it.
 *
 * ⚠️ THE KEY, not a label plus a flag beside it. The hedge and the figure
 * have to travel together or the component can pair them wrongly, and
 * "a value and the thing that decides how it is said, separable at the call
 * site" is the exact defect that took nine versions to close on the bell
 * side (`cleanup-bell-figures.ts`): twice a mutant stated a ceiling as a
 * floor and every guard stayed green. Returning the key leaves the
 * component with nothing to decide and the test asserting the sentence that
 * will actually be shown.
 */
export interface StatedFigure {
  /** An `i18next` key under `settings.storage`. */
  key: string
  /** Already formatted, for `{{size}}`. */
  size: string
}

export interface CleanupRowFigures {
  /** The members' figure, where the row states it on its own. */
  released?: StatedFigure
  /** The members' figure, where the organisation's is stated beside it. */
  releasedMembers?: StatedFigure
  /** The organisation's figure. Never present without `releasedMembers`. */
  orgReleased?: StatedFigure
  /** The ceiling, where there is no members' figure. */
  ceiling?: StatedFigure
  /** The sentence explaining the gap. Present exactly with `orgReleased`. */
  sharedNote?: boolean
}

/** Every key this rule can return, so the suite can pin the set. */
export const ROW_FIGURE_KEYS = {
  releasedExact: 'settings.storage.cleanupResultReleasedExact',
  releasedAtLeast: 'settings.storage.cleanupResultReleased',
  membersExact: 'settings.storage.cleanupResultReleasedMembersExact',
  membersAtLeast: 'settings.storage.cleanupResultReleasedMembers',
  orgExact: 'settings.storage.cleanupResultReleasedOrg',
  orgAtLeast: 'settings.storage.cleanupResultReleasedOrgAtLeast',
  ceiling: 'settings.storage.cleanupResultFreed',
} as const

export function cleanupRowFigures(
  row: {
    /** Bytes that left members' own figures. `null` = never measured. */
    releasedBytes?: number | null
    /** Bytes that left the organisation's figure. `null` = never measured. */
    orgReleasedBytes?: number | null
    /** Bytes the deleted conversations held. `null` = never measured. */
    freedBytes?: number | null
    /** A chunk dropped the members' figure. `null` = unknown ⇒ hedge. */
    releasedLost?: boolean | null
    /** A chunk dropped the organisation's figure. `null` = unknown ⇒ hedge. */
    orgReleasedLost?: boolean | null
    /**
     * Who the cleanup was for. Only the KIND is read, and only to answer
     * one question: is there a member whose figure the organisation's could
     * be mistaken for? For `unattributed` there is not — see rule 4.
     */
    target?: { kind: 'member' | 'unattributed' }
  },
  /**
   * How the row renders a byte count. Taken as a parameter so the rule can
   * be exercised without pulling in the formatter's own rounding — and so
   * rule 3 is applied to what the reader actually sees.
   */
  formatBytes: (n: number) => string,
  /**
   * The booking has stopped moving, so its totals are final.
   *
   * Taken as a parameter rather than derived from the row's state here:
   * "is this cleanup still going" is the server's own rule and already
   * lives in `activityUnderWay` (`cleanupVerdict.ts`), which knows about
   * `resumed` and `due` rows that look scheduled but are mid-run. This
   * function must not grow a second copy of it — but it must not state an
   * exact figure without it either.
   */
  settled: boolean,
): CleanupRowFigures {
  const released = typeof row.releasedBytes === 'number' && row.releasedBytes > 0 ? formatBytes(row.releasedBytes) : null
  const org = typeof row.orgReleasedBytes === 'number' && row.orgReleasedBytes > 0 ? formatBytes(row.orgReleasedBytes) : null
  const held = typeof row.freedBytes === 'number' && row.freedBytes > 0 ? formatBytes(row.freedBytes) : null

  // Rule 5, both halves. `=== false` and not `!flag`: only an explicit
  // false says no chunk was dropped; `null`/`undefined` (unknown) hedges.
  // And `settled`, because a total that is still growing is not exact
  // however clean it is.
  const releasedExact = settled && row.releasedLost === false
  const orgExact = settled && row.orgReleasedLost === false

  if (released === null) {
    // Rule 4, and its ONE exception.
    //
    // Normally the organisation's figure is never shown alone: with no
    // members' figure beside it (a booking older than the columns) it would
    // be read as what the member got back — the overstatement these columns
    // exist to remove.
    //
    // ⚠️ AN UNATTRIBUTED BOOKING HAS NO MEMBER TO BE MISREAD AS. Its target
    // is DEFINED as the files with no attributable uploader, so
    // `memberBytesReleased` discards every row it holds and the members'
    // figure is structurally NULL — forever, for every such booking. Rule 4
    // then fell through to the plain held sum, which cancelled the whole
    // point of computing the tenant figure for exactly the target where it
    // is the only meaningful number. (The commit that stopped the read
    // filtering uploader-less rows claimed this was fixed; it was half
    // fixed, and this is the other half.)
    //
    // Only for `unattributed`. A MEMBER booking whose member figure went
    // missing keeps the old behaviour: showing the organisation's figure
    // alone on her row is the misreading above.
    if (row.target?.kind === 'unattributed' && org !== null) {
      return { orgReleased: { key: orgExact ? ROW_FIGURE_KEYS.orgExact : ROW_FIGURE_KEYS.orgAtLeast, size: org } }
    }
    // The ceiling is a plain sum of what was held — its own kind of bound,
    // and its copy already says "up to", so it takes no hedge from rule 5.
    return held === null ? {} : { ceiling: { key: ROW_FIGURE_KEYS.ceiling, size: held } }
  }

  // Rule 3b, asked on the raw bytes before anything is rendered: the two
  // columns can disagree with their own relation (see the docblock), and a
  // figure contradicting the sentence beneath it is not a figure to state.
  const orgIsBelow = typeof row.orgReleasedBytes === 'number'
    && typeof row.releasedBytes === 'number'
    && row.orgReleasedBytes < row.releasedBytes

  // Rule 3c — THE MIRROR OF 3b, and it was missed because 3b was written
  // for one direction only.
  //
  // 3b asks "is the gap pointing the right way". This asks "is the gap
  // EXPLAINABLE". The sentence that ships with the second figure names ONE
  // cause: the same file counted against two members' limits. When the
  // ORGANISATION's figure lost a chunk and the members' did not, the gap is
  // mostly a DROPPED MEASUREMENT and that sentence names a cause that does
  // not exist — three chunks, one organisation-query failure, nobody
  // sharing any file, and the admin reads
  //
  //     members' usage dropped by 900 MB · the organisation got back at
  //     least 500 MB
  //     A file more than one member uploaded counts against each of their
  //     limits but is stored once…
  //
  // while the organisation's own storage total moved by the full 900 MB.
  // "At least" is true but reads as a caveat on a real gap rather than as
  // the gap itself. So an unattributable gap is not stated: rule 1's "a
  // figure that contradicts its own sentence is no figure to state",
  // applied in the direction 3b did not cover.
  const gapIsExplainable = !(row.orgReleasedLost === true && row.releasedLost !== true)

  if (org !== null && orgIsBelow && gapIsExplainable && org !== released) {
    // Rules 2 and 3: the gap is real, it points the right way, and it is
    // visible AS SHOWN — so both figures and the sentence go out together.
    return {
      releasedMembers: { key: releasedExact ? ROW_FIGURE_KEYS.membersExact : ROW_FIGURE_KEYS.membersAtLeast, size: released },
      orgReleased: { key: orgExact ? ROW_FIGURE_KEYS.orgExact : ROW_FIGURE_KEYS.orgAtLeast, size: org },
      sharedNote: true,
    }
  }
  return { released: { key: releasedExact ? ROW_FIGURE_KEYS.releasedExact : ROW_FIGURE_KEYS.releasedAtLeast, size: released } }
}
